/** Dashboard (D-078, design v2.1): today's figures for the agency (with the prospects trend when the
 * data allows it), then a bento grid that always fills its rows: today's appointments and the
 * deadline alerts, the recent files and the prospects pipeline. */

import {
  type Lead,
  useAppointmentsItemsList,
  useAppointmentsTicketsList,
  useAuthMeRetrieve,
  useCrmLeadsList,
  useCrmLeadsPipelineRetrieve,
  useImmigrationAlertsHandleCreate,
  useImmigrationAlertsList,
  useImmigrationDossiersList,
  useImmigrationDossiersSummaryRetrieve,
  useInboxConversationsSummaryRetrieve,
} from "@agencyos/api-client";
import { Avatar, Badge, Button, EmptyState, GlassCard, Skeleton, SkeletonText, StatCard, type StatTone, type StatTrend } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import {
  BellRing,
  CalendarDays,
  ConciergeBell,
  FileCheck2,
  type LucideIcon,
  MessageSquareText,
  UsersRound,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Page } from "../components/AppShell";
import { localized } from "../components/TranslatedFields";
import { session, useSession } from "../lib/session";
import { MissedCallsBanner } from "./calls/MissedCallsBanner";

const DAY_MS = 86_400_000;
const PIPELINE = ["new", "qualified", "appointment", "assessment", "contract_sent", "client"] as const;

function Kpi({
  to,
  icon: Icon,
  label,
  value,
  hint,
  tone,
  trend,
  series,
  seriesLabel,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  value?: number;
  hint?: string;
  tone: StatTone;
  trend?: StatTrend;
  series?: number[];
  seriesLabel?: string;
}) {
  return (
    <StatCard
      className="kpi ao-pressable"
      tone={tone}
      icon={<Icon size={18} strokeWidth={1.75} />}
      label={label}
      value={value === undefined ? <Skeleton style={{ width: 44, height: 28 }} /> : value}
      hint={hint}
      trend={trend}
      series={series}
      seriesLabel={seriesLabel}
      wrap={(inner) => (
        <Link to={to} className="ao-stat__link kpi__link">
          {inner}
        </Link>
      )}
    />
  );
}

/** New prospects per Douala day over the last 14 days, and this week against the previous one.
 * Only when the whole list is loaded (otherwise the curve would be wrong). */
function leadTrend(leads: readonly Lead[] | undefined, complete: boolean) {
  if (!leads || !complete || !leads.length) return null;
  const day = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Douala" }).format(d);
  const today = new Date();
  const keys = Array.from({ length: 14 }, (_, i) => day(new Date(today.getTime() - (13 - i) * DAY_MS)));
  const counts = new Map(keys.map((k) => [k, 0]));
  for (const lead of leads) {
    const key = day(new Date(lead.created_at));
    if (counts.has(key)) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const series = keys.map((k) => counts.get(k) ?? 0);
  const thisWeek = series.slice(7).reduce((a, b) => a + b, 0);
  const lastWeek = series.slice(0, 7).reduce((a, b) => a + b, 0);
  const delta = thisWeek - lastWeek;
  return { series, delta, direction: delta > 0 ? ("up" as const) : delta < 0 ? ("down" as const) : ("flat" as const) };
}

function CardHead({ title, action, count }: { title: string; action?: ReactNode; count?: number }) {
  return (
    <div className="ao-card__header">
      <h2 className="ao-card__title">
        {title}
        {count ? <span className="ao-count ao-count--muted">{count}</span> : null}
      </h2>
      {action}
    </div>
  );
}

export function HomePage() {
  const { t, i18n } = useTranslation();
  const me = useAuthMeRetrieve();
  const { organization } = useSession();
  const queryClient = useQueryClient();
  const user = me.data?.status === 200 ? me.data.data : undefined;
  const membership = user?.memberships.find((m) => m.organization_slug === organization) ?? user?.memberships[0];
  const works = ["owner", "manager", "advisor"].includes(membership?.role ?? "");

  const inbox = useInboxConversationsSummaryRetrieve({ query: { enabled: works } });
  const leads = useCrmLeadsList(undefined, { query: { enabled: works } });
  const today = useAppointmentsItemsList({ days: 1 }, { query: { enabled: works } });
  const tickets = useAppointmentsTicketsList(undefined, { query: { enabled: works } });
  const dossiers = useImmigrationDossiersList(undefined, { query: { enabled: works } });
  // D-115: the lists are paginated, so the figures come from counts made by the API.
  const stageCounts = useCrmLeadsPipelineRetrieve({ query: { enabled: works } });
  const fileCounts = useImmigrationDossiersSummaryRetrieve({ query: { enabled: works } });
  const alerts = useImmigrationAlertsList({ query: { enabled: works } });
  const handle = useImmigrationAlertsHandleCreate({
    mutation: { onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["/api/v1/immigration/alerts/"] }) },
  });
  const pending = alerts.data?.status === 200 ? alerts.data.data : undefined;

  const unread = inbox.data?.status === 200 ? inbox.data.data.unread_messages : undefined;
  const leadPage = leads.data?.status === 200 ? leads.data.data : undefined;
  const trend = leadTrend(leadPage?.results, Boolean(leadPage && leadPage.count === leadPage.results.length));
  const appointments = today.data?.status === 200 ? today.data.data.results.filter((a) => a.status !== "cancelled") : undefined;
  const waiting = tickets.data?.status === 200 ? tickets.data.data.filter((x) => x.status === "waiting").length : undefined;
  const files = dossiers.data?.status === 200 ? dossiers.data.data.results : undefined;
  const openFiles = fileCounts.data?.status === 200 ? fileCounts.data.data.open : undefined;
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  const time = (iso: string) => new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Douala" });
  const todayText = new Date().toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", timeZone: "Africa/Douala" });
  const stages = stageCounts.data?.status === 200 ? stageCounts.data.data.stages : undefined;
  const pipeline = PIPELINE.map((stage) => ({ stage, count: stages?.[stage] ?? 0 }));
  // « In the pipeline »: every lead still worked on (the lost ones are not counted).
  const leadCount = stages ? pipeline.reduce((sum, row) => sum + row.count, 0) : undefined;
  const pipelineMax = Math.max(1, ...pipeline.map((p) => p.count));

  if (me.isPending) {
    return (
      <Page>
        <Skeleton style={{ width: "40%", height: 32 }} />
        <div className="kpis">
          {[0, 1, 2, 3, 4].map((n) => (
            <Skeleton key={n} style={{ height: 124, borderRadius: 22 }} />
          ))}
        </div>
      </Page>
    );
  }
  if (!user) {
    return (
      <Page>
        <GlassCard role="alert">{t("common.error")}</GlassCard>
      </Page>
    );
  }

  const seeAll = (to: string) => (
    <Link className="ao-button ao-button--ghost ao-button--sm" to={to}>
      {t("dashboard.seeAll")}
    </Link>
  );

  return (
    <Page
      eyebrow={<span className="home__date">{todayText}</span>}
      title={t("home.welcome", { name: user.full_name || user.email })}
      description={membership ? t("dashboard.intro", { agency: membership.organization_name }) : t("home.noAgency")}
      actions={
        user.memberships.length > 1
          ? user.memberships
              .filter((m) => m.organization_slug !== organization)
              .map((m) => (
                <Button
                  key={m.organization_slug}
                  variant="secondary"
                  onClick={() => {
                    session.setOrganization(m.organization_slug ?? null);
                    void queryClient.invalidateQueries();
                  }}
                >
                  {t("home.switchTo", { name: m.organization_name })}
                </Button>
              ))
          : null
      }
    >
      {works ? (
        <>
          <MissedCallsBanner />
          <section className="kpis" aria-label={t("dashboard.figures")}>
            <Kpi to="/inbox" tone="success" icon={MessageSquareText} label={t("dashboard.unread")} value={unread} hint={t("dashboard.unreadHint")} />
            <Kpi
              to="/leads"
              tone="accent"
              icon={UsersRound}
              label={t("dashboard.leads")}
              value={leadCount}
              hint={t("dashboard.leadsHint")}
              series={trend?.series}
              seriesLabel={t("dashboard.leadsSeries")}
              trend={
                trend
                  ? {
                      value: t("dashboard.newThisWeek", { count: trend.series.slice(7).reduce((a, b) => a + b, 0) }),
                      direction: trend.direction,
                      label: t("dashboard.vsLastWeek", { delta: trend.delta > 0 ? `+${trend.delta}` : String(trend.delta) }),
                    }
                  : undefined
              }
            />
            <Kpi to="/agenda" tone="info" icon={CalendarDays} label={t("dashboard.appointments")} value={appointments?.length} hint={t("dashboard.today")} />
            <Kpi to="/reception" tone="warning" icon={ConciergeBell} label={t("dashboard.waiting")} value={waiting} hint={t("dashboard.atOffice")} />
            <Kpi to="/dossiers" tone="neutral" icon={FileCheck2} label={t("dashboard.files")} value={openFiles} hint={t("dashboard.filesHint")} />
          </section>

          <div className="bento">
            <GlassCard as="section" className="stack bento__wide">
              <CardHead title={t("dashboard.todayAgenda")} count={appointments?.length} action={seeAll("/agenda")} />
              {today.isPending ? <SkeletonText lines={4} /> : null}
              {appointments && !appointments.length ? (
                <EmptyState compact icon={<CalendarDays size={22} strokeWidth={1.75} />} title={t("dashboard.noAppointment")} />
              ) : null}
              {appointments?.length ? (
                <ol className="timeline">
                  {appointments.map((a) => (
                    <li key={a.id} className="timeline__row">
                      <time className="timeline__time ao-num" dateTime={a.starts_at}>
                        {time(a.starts_at)}
                      </time>
                      <span className="timeline__body">
                        <strong>{a.contact_name || a.contact_phone}</strong>
                        <span className="muted">{localized(a.type_name, i18n.language)}</span>
                      </span>
                      <Badge tone={a.mode === "office" ? "accent" : "default"}>{t(`agenda.mode.${a.mode}`)}</Badge>
                    </li>
                  ))}
                </ol>
              ) : null}
            </GlassCard>

            <GlassCard as="section" className="stack bento__narrow">
              <CardHead title={t("alerts.title")} count={pending?.length} />
              {alerts.isPending ? <SkeletonText lines={3} /> : null}
              {pending && !pending.length ? (
                <EmptyState compact icon={<BellRing size={22} strokeWidth={1.75} />} title={t("alerts.none")} />
              ) : null}
              {pending?.length ? (
                <ul className="plain-list alert-list">
                  {pending.slice(0, 6).map((a) => (
                    <li key={a.id} className={`alert-row alert-row--${a.state === "expired" ? "expired" : "soon"}`}>
                      <span className="alert-row__dot" aria-hidden="true" />
                      <span className="alert-row__body">
                        <Link className="link" to={`/dossiers/${a.dossier}`}>
                          {a.client_name}
                        </Link>
                        <span className="muted">{localized(a.title, i18n.language)}</span>
                        <Badge tone={a.state === "expired" ? "danger" : "warning"}>
                          {t(`alerts.state.${a.state === "expired" ? "expired" : "soon"}`)} ·{" "}
                          {new Date(a.due).toLocaleDateString(locale, { timeZone: "Africa/Douala" })}
                        </Badge>
                      </span>
                      <Button variant="secondary" size="sm" disabled={handle.isPending} onClick={() => handle.mutate({ id: a.id })}>
                        {t("alerts.handle")}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </GlassCard>

            <GlassCard as="section" className="stack bento__wide">
              <CardHead title={t("dashboard.recentFiles")} action={seeAll("/dossiers")} />
              {dossiers.isPending ? <SkeletonText lines={4} /> : null}
              {files && !files.length ? <EmptyState compact icon={<FileCheck2 size={22} strokeWidth={1.75} />} title={t("ee.none")} /> : null}
              {files?.length ? (
                <ul className="plain-list file-list">
                  {files.slice(0, 6).map((d) => (
                    <li key={d.id}>
                      <Link className="file-row" to={`/dossiers/${d.id}`}>
                        <Avatar name={d.contact_name || d.contact_phone} />
                        <span className="file-row__name">
                          <strong>{d.contact_name || d.contact_phone}</strong>
                          <span className="muted ao-num">{d.reference}</span>
                        </span>
                        <Badge dot tone={d.status === "submitted" || d.status === "validated" ? "success" : d.status === "review" ? "info" : "default"}>
                          {t(`ee.status.${d.status}`)}
                        </Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </GlassCard>

            <GlassCard as="section" className="stack bento__narrow">
              <CardHead title={t("dashboard.pipeline")} action={seeAll("/leads")} />
              <p className="ao-card__subtitle">{t("dashboard.pipelineHint")}</p>
              {stageCounts.isPending ? <SkeletonText lines={5} /> : null}
              {stages ? (
                <ul className="plain-list pipeline">
                  {pipeline.map(({ stage, count }) => (
                    <li key={stage} className="pipeline__row">
                      <span className="pipeline__label">{t(`crm.stage.${stage}`)}</span>
                      <span className="pipeline__bar" aria-hidden="true">
                        <span style={{ transform: `scaleX(${count / pipelineMax})` }} />
                      </span>
                      <span className="pipeline__count ao-num">{count}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </GlassCard>
          </div>
        </>
      ) : (
        <GlassCard>
          <p className="muted">{membership ? t("dashboard.limited") : t("home.noAgency")}</p>
        </GlassCard>
      )}
    </Page>
  );
}
